import Scenes from "../enums/Scenes";
import ContainmentRect from "../game/ContainmentRect";
import Game from "../game/Game";
import GameCore from "../game/GameCore";
import VirtualJoystick from "phaser3-rex-plugins/plugins/virtualjoystick.js";
import NarrationController from "../../components/Narration/NarrationController";
import { SpecialDealsService } from "../../services/SpecialDealsService";
import { showSpecialDealsModal } from "../../utils/modal/ModalManager";
import { profiler } from "../../utils/PerformanceProfiler";
import { useGameStore } from "../../store/gameStore";
import StageType from "../enums/StageType";

export const STAGES_WITH_SPECIAL_DEALS: string[] = [
  StageType.DESERT3,
  // Add more stages as needed
];

export default class MainScene extends Phaser.Scene {
  cursors: Phaser.Types.Input.Keyboard.CursorKeys;
  wKey: Phaser.Input.Keyboard.Key;
  aKey: Phaser.Input.Keyboard.Key;
  sKey: Phaser.Input.Keyboard.Key;
  dKey: Phaser.Input.Keyboard.Key;
  pointer: Phaser.Input.Pointer;
  _lastVelocity: Phaser.Math.Vector2;
  cIndex: number;
  cursorsVector: Phaser.Math.Vector2;
  wasDown: boolean;
  maxSpeed: number;
  containmentRect_Enemies: ContainmentRect;
  containmentRect_Bullets: ContainmentRect;
  joyStick: VirtualJoystick & { isActive: boolean };
  private backgroundMusic: Phaser.Sound.BaseSound;
  private shouldConsumeEnergy: boolean = true; // Flag to control energy consumption
  private containmentCheckCounter: number = 0;

  // Performance diagnostic properties
  private performanceDiagnostic: boolean = false;
  private frameTimeStart: number = 0;
  private frameTimeThreshold: number = 16.67; // 60 FPS = 16.67ms per frame

  constructor() {
    super({ key: Scenes.MainScene });
    this._lastVelocity = new Phaser.Math.Vector2(1, 0);
    this.cIndex = 0;
    this.cursorsVector = new Phaser.Math.Vector2(0, 0);
    this.wasDown = false;

    // Enable performance diagnostic in debug mode
    this.performanceDiagnostic = localStorage.getItem("debugMode") === "true";
  }

  init(data?: { consumeEnergy?: boolean }) {
    // Set whether energy should be consumed based on init data
    this.shouldConsumeEnergy = data?.consumeEnergy !== false;
  }

  create() {
    // Start performance profiling
    profiler.start();

    // Hide loading screen immediately when scene is created
    if (typeof (window as any).hideLoadingScreen === "function") {
      (window as any).hideLoadingScreen();
    }

    // Create a larger outer ring with just a border - now with thicker border
    const outerRing = this.add
      .circle(0, 0, 100, 0xeeeeee, 0)
      .setStrokeStyle(8, 0xeeeeee, 0.9) // Increased thickness from 3 to 5 and opacity from 0.7 to 0.8
      .setDepth(10001);

    // Create a smaller inner thumb
    const innerThumb = this.add.circle(0, 0, 30, 0xffffff, 0.9).setDepth(1e4);

    this.joyStick = new VirtualJoystick(this, {
      x: this.cameras.main.width / 2,
      y: this.cameras.main.height - 250, // Moved up from -100 to -250
      radius: 100,
      base: outerRing,
      thumb: innerThumb,
      forceMin: 8,
      enable: false,
    }) as VirtualJoystick & { isActive: boolean };
    this.joyStick.isActive = false;

    if (this.input.keyboard) {
      this.cursors = this.input.keyboard.createCursorKeys();
      this.wKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W);
      this.aKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A);
      this.sKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S);
      this.dKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D);
    }

    this.pointer = this.input.activePointer;
    Game.Core.InitGame(this);

    this.containmentRect_Enemies = new ContainmentRect(1);
    this.containmentRect_Bullets = new ContainmentRect(0.55);
    this.cameras.main.startFollow(Game.Core.Player);

    // Play music if enabled
    if (Game.Core?.PlayerOptions?.MusicEnabled !== false) {
      this.backgroundMusic = this.sound.add("desert_music", {
        volume: 0.4,
        loop: true,
      });
      this.backgroundMusic.play();
    }

    //Enable for immortality (for testing)
    //Game.Core.Player.SetInvulForMilliSeconds(Number.MAX_VALUE)

    // Register narration events with the game's event system
    NarrationController.registerNarrationEvents();

    // Only consume energy if this is a new game start or retry (not revive)
    if (this.shouldConsumeEnergy) {
      useGameStore.getState().decreaseEnergy();
    }

    const currentStage = Game.Core.PlayerOptions.SelectedStage;

    // Only show stage-specific special deals if the service hasn't already shown deals
    // This prevents duplicate special deals dialogs in the same session
    if (
      STAGES_WITH_SPECIAL_DEALS.includes(currentStage) &&
      !SpecialDealsService.isEligibleForSpecialDeals()
    ) {
      // Pause the game before showing special deals modal
      Game.Core.SceneManager?.scene.pause(Scenes.MainScene);

      // Show Special Deals for specific stages (like Desert3)
      showSpecialDealsModal(() => {
        // After successful purchase, update boost counts in the game UI
        if (Game.Core?.MainUI?.updateBoostCounts) {
          Game.Core.MainUI.updateBoostCounts();
        }
      }, true); // Hide shop button during gameplay
      return;
    }
  }

  update(time: number, delta: number): void {
    // Performance diagnostic - start timing
    if (this.performanceDiagnostic) {
      this.frameTimeStart = performance.now();
    }

    // Mark the start of scene update (input handling, physics setup)
    profiler.markRenderStart();

    this.maxSpeed = GameCore.PlayerPxSpeed * Game.Core.Player.moveSpeed;
    const pointer = this.pointer.isDown
      ? this.pointer
      : this.input.pointer1.isDown
      ? this.input.pointer1
      : null;

    if (pointer && pointer.isDown) {
      if (!this.joyStick.enable || !this.joyStick.isActive) {
        this.joyStick.enable = true;
        this.joyStick.isActive = true;
        this.joyStick.setPosition(pointer.x, pointer.y);
      }

      if (this.joyStick.force > 8) {
        this.physics.velocityFromRotation(
          this.joyStick.rotation,
          this.maxSpeed,
          Game.Core.Player.Body.velocity
        );
        this._lastVelocity.copy(
          Game.Core.Player.body?.velocity || this._lastVelocity
        );
      }
    } else {
      if (this.joyStick.isActive) {
        this.joyStick.setPosition(
          this.cameras.main.width / 2,
          this.cameras.main.height - 250
        );
        this.joyStick.enable = false;
        this.joyStick.isActive = false;
      }

      let flag = false;
      this.cursorsVector.x = 0;
      this.cursorsVector.y = 0;

      if (Game.Core.Player) {
        flag = this.updateCursors(delta);

        if (flag) {
          Game.Core.Player.setVelocity(
            this.cursorsVector.x,
            this.cursorsVector.y
          );
          this._lastVelocity.copy(
            Game.Core.Player?.body?.velocity || this._lastVelocity
          );
        } else {
          Game.Core.Player.setVelocity(0, 0);
          Game.Core.Player.lastFaceDirection = this._lastVelocity;
        }
      }
    }

    if (!Game.Core.IsTimeStopped) {
      // Optimize containment rect checks - reduce frequency and iterations
      this.containmentCheckCounter++;

      // Only run containment checks every 3rd frame to reduce CPU load
      if (this.containmentCheckCounter % 3 === 0) {
        // Reduce iterations from 10 to 3 per frame when checks run
        for (let e = 0; e < 3; e++) {
          this.containmentRect_Enemies.DespawnIfOutside(
            Game.Core.EnemyGroup.children.entries
          );
        }
        for (let e = 0; e < 3; e++) {
          this.containmentRect_Bullets.DespawnIfOutside(
            Game.Core.BulletGroup.children.entries
          );
        }
      }
    }

    // Mark end of scene render/input processing
    profiler.markRenderEnd();

    // Game core update is already being profiled internally
    const gameUpdateStart = this.performanceDiagnostic ? performance.now() : 0;
    Game.Core.Update(delta);

    // Performance diagnostic - end timing and report
    if (this.performanceDiagnostic) {
      const frameTime = performance.now() - this.frameTimeStart;
      const gameUpdateTime = performance.now() - gameUpdateStart;

      if (frameTime > this.frameTimeThreshold) {
        console.warn(
          `🐌 SLOW FRAME: ${frameTime.toFixed(
            2
          )}ms (Target: ${this.frameTimeThreshold.toFixed(2)}ms)`
        );
        console.log(`  - Game Update: ${gameUpdateTime.toFixed(2)}ms`);
        console.log(
          `  - Scene Input/Render: ${(frameTime - gameUpdateTime).toFixed(2)}ms`
        );
        console.log(`  - Enemies: ${Game.Core.Enemies.length}`);
        console.log(`  - Bullets: ${Game.Core.BulletGroup.children.size}`);
        console.log(`  - Pickups: ${Game.Core.PickupGroup.children.size}`);
      }
    }
  }

  updateCursors(delta) {
    let flag = false;
    if (this.cursors.left.isDown || this.aKey.isDown) {
      this.cursorsVector.x = -1;
      flag = true;
    } else if (this.cursors.right.isDown || this.dKey.isDown) {
      this.cursorsVector.x = 1;
      flag = true;
    }
    if (this.cursors.up.isDown || this.wKey.isDown) {
      this.cursorsVector.y = -1;
      flag = true;
    } else if (this.cursors.down.isDown || this.sKey.isDown) {
      this.cursorsVector.y = 1;
      flag = true;
    }
    if (this.cursorsVector.x !== 0 && this.cursorsVector.y !== 0) {
      this.cursorsVector.normalize();
    }
    this.cursorsVector.x *= this.maxSpeed;
    this.cursorsVector.y *= this.maxSpeed;
    return flag;
  }

  destroy() {
    // Stop and destroy music
    if (this.backgroundMusic) {
      this.backgroundMusic.stop();
      this.backgroundMusic.destroy();
    }

    // Clean up narration events
    NarrationController.cleanupNarrationEvents();

    // Clean up enemy event listeners
    Game.Core.Enemies.forEach((enemy) => {
      if (enemy && enemy.removeAllListeners) {
        enemy.removeAllListeners();
      }
    });
  }

  shutdown() {
    // Also clean up when scene is shutdown but not destroyed
    NarrationController.cleanupNarrationEvents();

    // Clean up input event listeners
    if (this.joyStick) {
      this.joyStick.enable = false;
    }

    // Ensure we don't have any stray event listeners
    this.events.removeAllListeners();
  }
}
